import { ArrowsClockwise } from '@phosphor-icons/react';
import { ColDef, IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable, Button } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { useCostDecimals } from 'pricing/hooks';
import { TradeAgreementUsage } from 'quotes/enums';
import { ITradeAgreement } from 'quotes/types';
import { isTradeAgreementExpired } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { getColumnDef, getRowClassRules } from './columns';
import { PreselectHeldAgreement } from './PreselectHeldAgreement';
import {
  ITradeAgreementsNoRowsOverlayParams,
  TradeAgreementsNoRowsOverlay,
} from './TradeAgreementsNoRowsOverlay';
import './style.css';
import {
  getTradeAgreementRowId,
  getTradeAgreementUsage,
  ITradeAgreementUsageContext,
} from '../../utils';

interface IProps {
  agreements: ITradeAgreement[];
  heldRowId: string | null;
  defaultRowId?: string | null;
  noRowsMessage: string;
  noRowsReason?: string | null;
  onSelectionChanged: (event: SelectionChangedEvent<ITradeAgreement>) => void;
  onUse: (agreement: ITradeAgreement) => void;
  onRefresh?: () => void;
  isLoading: boolean;
  isRefreshing?: boolean;
  isDefaultInUse?: boolean;
}

// AG-Grid toggles the row on Space from any of its cells, which would uncheck the chosen radio.
// Module level: the provider memoizes the merged defaults on this object's identity.
const tradeAgreementsDefaultColDef: ColDef = {
  suppressKeyboardEvent: ({ event, node }) => ' ' === event.key && !!node.isSelected(),
};

export const SpaListTable = ({
  agreements,
  heldRowId,
  defaultRowId = null,
  noRowsMessage,
  noRowsReason = null,
  onSelectionChanged,
  onUse,
  onRefresh,
  isLoading,
  isRefreshing = false,
  isDefaultInUse = false,
}: IProps) => {
  // A stable handler, so a new `onUse` identity from the host does not rebuild the column defs.
  const onUseRef = useRef(onUse);
  onUseRef.current = onUse;

  const { t } = useTranslation();

  const costDecimals = useCostDecimals();

  const handleOnUse = useCallback((agreement: ITradeAgreement) => onUseRef.current(agreement), []);

  const usageContext = useMemo<ITradeAgreementUsageContext>(
    () => ({ defaultRowId, heldRowId, isDefaultInUse }),
    [defaultRowId, heldRowId, isDefaultInUse]
  );

  const columnDefs = useMemo(
    () => getColumnDef(costDecimals, { ...usageContext, onUse: handleOnUse }),
    [costDecimals, usageContext, handleOnUse]
  );

  // Neither an expired agreement nor the default in use can be picked, by any route.
  const isAgreementSelectable = useCallback(
    ({ data }: IRowNode<ITradeAgreement>) =>
      !isTradeAgreementExpired(data) &&
      TradeAgreementUsage.DEFAULT_IN_USE !== getTradeAgreementUsage(data, usageContext),
    [usageContext]
  );

  const getRowId = useCallback(
    (data: AgGridDataRow) => getTradeAgreementRowId(data as unknown as ITradeAgreement),
    []
  );

  const rowClassRules = useMemo(() => getRowClassRules(usageContext), [usageContext]);

  // Memoized: AG-Grid refreshes the shown overlay whenever this grid option changes identity.
  const noRowsOverlayParams = useMemo<ITradeAgreementsNoRowsOverlayParams>(
    () => ({ message: noRowsMessage, reason: noRowsReason }),
    [noRowsMessage, noRowsReason]
  );

  const actionButtons = useCallback(
    () =>
      onRefresh
        ? [
            <Button
              key="refreshTradeAgreementsButton"
              icon={<ArrowsClockwise />}
              variant={Variant.OUTLINED}
              color={Color.NEUTRAL}
              loading={isRefreshing}
              onClick={onRefresh}
              aria-label={t('tooltipRefreshReport')}
              tooltipProps={{ title: t('tooltipRefreshReport'), children: null }}
            />,
          ]
        : [],
    [isRefreshing, onRefresh, t]
  );

  // Its own selection count: the host quote grid's count must not follow a row picked here.
  return (
    <SelectionCountProvider>
      <AgGridClientTableProvider
        tableId="quotePartTradeAgreementsTable"
        entityType="TradeAgreement"
        layoutType={TableLayoutType.QUOTE_PART_TRADE_AGREEMENTS}
        columnDefs={columnDefs}
        defaultColDef={tradeAgreementsDefaultColDef}
        data={agreements as unknown as AgGridDataRow[]}
        isDataLoading={isLoading}
        loadingPercentage={isLoading ? 0 : 100}
        dataCurrentPage={1}
        getRowId={getRowId}
        rowClassRules={rowClassRules}
        onSelectionChanged={onSelectionChanged}
        selectionMode="singleRow"
        isRowSelectable={isAgreementSelectable}
        // The radio column replaces the grid's own checkbox column.
        hasRowSelection={false}
        enableClickSelection
        hasGroupByEnabled={false}
        hasLayoutActions
        noRowsOverlayComponent={TradeAgreementsNoRowsOverlay}
        noRowsOverlayComponentParams={noRowsOverlayParams}
      >
        <PreselectHeldAgreement heldRowId={heldRowId} />

        <AgGridTable actionButtons={actionButtons} />
      </AgGridClientTableProvider>
    </SelectionCountProvider>
  );
};
